import React from 'react';
import { Tooltip } from 'antd';
import {
  EyeOutlined,
  FileTextOutlined,
  CheckOutlined,
  ToolOutlined,
  CheckCircleFilled,
  CloseCircleFilled,
} from '@ant-design/icons';

// Fase 2 del roadmap Hallazgos->Presupuesto->ODT.
// Visualiza la etapa del hallazgo (maquina de estados backend Fase 1)
// como dots conectados por linea. Variante "compact" va en tabla (sin labels),
// variante "full" en drawer/detalle (con labels + tooltip de fechas).
//
// Los 6 estados se colapsan a 5 dots visibles: DETECTADO es origen; RESUELTO
// cierra con check doble. DESCARTADO se muestra como un unico dot tachado
// en cualquier posicion posterior (no entra en el camino lineal).
//
// Colores derivados del whitTheme (industrial-precision): naranja industrial
// para EN_ODT, verde inspeccion para RESUELTO, azul info para PRESUPUESTADO.
// Sin dependencias externas — pura composicion de antd icons + CSS inline.

const ETAPAS = ['DETECTADO', 'PRESUPUESTADO', 'APROBADO', 'EN_ODT', 'RESUELTO'];

const ETAPA_META = {
  DETECTADO: { label: 'Detectado', icon: EyeOutlined, color: '#64748b' },
  PRESUPUESTADO: { label: 'Presupuestado', icon: FileTextOutlined, color: '#1b4f72' },
  APROBADO: { label: 'Aprobado', icon: CheckOutlined, color: '#2e6b3a' },
  EN_ODT: { label: 'En ODT', icon: ToolOutlined, color: '#c5561d' },
  RESUELTO: { label: 'Resuelto', icon: CheckCircleFilled, color: '#1e5429' },
  DESCARTADO: { label: 'Descartado', icon: CloseCircleFilled, color: '#94a3b8' },
};

const SEVERIDAD_BORDER = {
  BAJA: '#2e6b3a',
  MEDIA: '#1b4f72',
  ALTA: '#c5561d',
  CRITICA: '#9b2a1e',
};

const etapaIndex = (etapa) => {
  const idx = ETAPAS.indexOf(etapa);
  return idx === -1 ? 0 : idx;
};

function HallazgoTracker({ etapa = 'DETECTADO', severidad, variant = 'compact' }) {
  const isDescartado = etapa === 'DESCARTADO';
  const currentIdx = isDescartado ? -1 : etapaIndex(etapa);

  if (variant === 'full') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {ETAPAS.map((e, idx) => {
          const meta = ETAPA_META[e];
          const Icon = meta.icon;
          const reached = !isDescartado && idx <= currentIdx;
          const isCurrent = !isDescartado && idx === currentIdx;
          return (
            <div key={e} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: 14,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: reached ? meta.color : 'transparent',
                  border: `2px solid ${reached ? meta.color : '#cbd2d9'}`,
                  color: reached ? '#fff' : '#94a3b8',
                  transition: 'all 0.16s cubic-bezier(0.22, 0.8, 0.32, 1)',
                  boxShadow: isCurrent ? `0 0 0 4px ${meta.color}22` : 'none',
                }}
              >
                <Icon style={{ fontSize: 13 }} />
              </div>
              <div>
                <div
                  style={{
                    fontSize: 13,
                    fontWeight: isCurrent ? 600 : 500,
                    color: reached ? '#0f172a' : '#94a3b8',
                    letterSpacing: isCurrent ? 0.2 : 0,
                  }}
                >
                  {meta.label}
                </div>
              </div>
            </div>
          );
        })}
        {isDescartado ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 4 }}>
            <div
              style={{
                width: 28,
                height: 28,
                borderRadius: 14,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: ETAPA_META.DESCARTADO.color,
                color: '#fff',
              }}
            >
              <CloseCircleFilled style={{ fontSize: 13 }} />
            </div>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#475569' }}>
              Descartado manualmente
            </div>
          </div>
        ) : null}
      </div>
    );
  }

  // COMPACT: linea horizontal con 5 dots, conector entre cada par.
  const borderColor = severidad ? SEVERIDAD_BORDER[severidad] : null;
  return (
    <div
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 2,
        padding: severidad ? '3px 6px' : 0,
        borderRadius: 999,
        border: borderColor ? `1px solid ${borderColor}33` : 'none',
      }}
      aria-label={`Etapa ${etapa}`}
    >
      {ETAPAS.map((e, idx) => {
        const meta = ETAPA_META[e];
        const reached = !isDescartado && idx <= currentIdx;
        const isCurrent = !isDescartado && idx === currentIdx;
        return (
          <React.Fragment key={e}>
            <Tooltip title={meta.label} mouseEnterDelay={0.3}>
              <span
                style={{
                  width: isCurrent ? 10 : 7,
                  height: isCurrent ? 10 : 7,
                  borderRadius: '50%',
                  background: reached ? meta.color : '#eef1f4',
                  border: reached ? 'none' : '1px solid #cbd2d9',
                  display: 'inline-block',
                  transition: 'all 0.16s cubic-bezier(0.22, 0.8, 0.32, 1)',
                  boxShadow: isCurrent ? `0 0 0 3px ${meta.color}2e` : 'none',
                }}
              />
            </Tooltip>
            {idx < ETAPAS.length - 1 ? (
              <span
                style={{
                  width: 12,
                  height: 1,
                  background: reached && idx < currentIdx ? ETAPA_META[e].color : '#e2e6eb',
                  display: 'inline-block',
                  transition: 'background 0.16s',
                }}
              />
            ) : null}
          </React.Fragment>
        );
      })}
      {isDescartado ? (
        <Tooltip title="Descartado">
          <CloseCircleFilled style={{ color: ETAPA_META.DESCARTADO.color, marginLeft: 4 }} />
        </Tooltip>
      ) : null}
    </div>
  );
}

export default HallazgoTracker;
